/**
 * The browser sender of the Hernas Events API (contract v1 section 10, adapted). It keeps a
 * persisted queue in localStorage, sends batches of at most 100 events and 60 KB, backs off
 * 15 s / 1 min / 5 min / 15 min on a failure, and hands the last batch to `sendBeacon` when the
 * page goes away. It uses the PUBLIC key in the query string with `text/plain`, so a request is
 * CORS-simple (no preflight).
 *
 * The caller creates it only after the visitor accepted analytics (`music24Events.ts`).
 */
export type EventsPlatform = 'ios' | 'android' | 'web' | 'desktop' | 'server';
export type EventPropValue = string | number | boolean;

export interface EventsContext {
  platform: EventsPlatform;
  appVersion?: string;
  build?: string;
  osVersion?: string;
  deviceModel?: string;
  locale?: string;
  userId?: string;
  premium?: boolean;
}

export interface TrackOptions {
  screen?: string;
  value?: number;
  currency?: string;
  userId?: string;
  premium?: boolean;
}

export interface QueuedEvent {
  id: string;
  name: string;
  at: number;
  screen?: string;
  userId?: string;
  premium?: boolean;
  props?: Record<string, EventPropValue>;
  value?: number;
  currency?: string;
  /** The session of the event. Not sent per event: a batch holds one session (its context). */
  sessionId?: string;
}

type WireEvent = Omit<QueuedEvent, 'sessionId'>;

/** Key-value persistence. The browser gives localStorage; a test passes a map. */
export interface EventsStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/** The outside world of the client, so a test can drive it. */
export interface EventsEnvironment {
  now(): number;
  fetch(url: string, init: RequestInit): Promise<{ status: number; headers: { get(name: string): string | null } }>;
  sendBeacon?(url: string, body: Blob): boolean;
  setInterval(handler: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
  uuid(): string;
}

export interface EventsClientOptions {
  /** `HERNAS_EVENTS_URL`, no trailing slash. */
  baseUrl: string;
  /** `HERNAS_EVENTS_PUBLIC_KEY` (pk_music24_...). */
  key: string;
  context: EventsContext;
  storage: EventsStorage | null;
  environment: EventsEnvironment;
  /** Prints the events instead of sending them. */
  debug?: boolean;
  /**
   * The catalog's session event (role `session`). `track` puts it in front of the first event of
   * each new session, with the props this gives (`firstSession`: the first session of the install).
   */
  sessionEvent?: { name: string; props: (firstSession: boolean) => Record<string, EventPropValue> };
}

export const MAX_QUEUE = 1000;
export const MAX_AGE_MS = 72 * 3600 * 1000;
export const BATCH_EVENTS = 100;
export const BATCH_BYTES = 60 * 1024;
export const FLUSH_AT = 20;
export const FLUSH_EVERY_MS = 15_000;
export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const BACKOFF_MS = [15_000, 60_000, 300_000, 900_000];

/** The storage keys of the client. `reset()` removes all of them. */
export const EVENTS_STORAGE_PREFIX = 'hxEvents:music24:';
const STORAGE_KEYS = ['queue', 'installId', 'session', 'sending'] as const;
/** Another tab that sends now holds the send mark this long at most. */
const SEND_MARK_MS = 20_000;

export class EventsClient {
  private readonly opts: EventsClientOptions;

  private readonly env: EventsEnvironment;

  /** The queue of a page without storage. With storage, the stored queue is the truth (all tabs). */
  private memoryQueue: QueuedEvent[] = [];

  /** False when the browser blocks or fills storage: the queue then lives in memory only. */
  private readonly storageOk: boolean;

  private userId: string | undefined;

  private premium: boolean | undefined;

  private readonly installId: string;

  private sessionId = '';

  private lastActivity = 0;

  private sending = false;

  private failures = 0;

  private notBefore = 0;

  private readonly timer: unknown;

  private stopped = false;

  private readonly firstSession: boolean;

  private hadSession = false;

  constructor(opts: EventsClientOptions) {
    this.opts = opts;
    this.env = opts.environment;
    const storedInstall = this.read('installId');
    this.firstSession = storedInstall === null;
    this.installId = storedInstall ?? this.env.uuid();
    this.write('installId', this.installId);
    this.storageOk = this.read('installId') === this.installId;
    this.userId = opts.context.userId;
    this.premium = opts.context.premium;
    this.timer = this.env.setInterval(() => {
      this.flush().catch(() => undefined);
    }, FLUSH_EVERY_MS);
    this.flush().catch(() => undefined);
  }

  get currentInstallId(): string {
    return this.installId;
  }

  get pending(): readonly QueuedEvent[] {
    return this.loadQueue();
  }

  /**
   * Sign-in and sign-out. `undefined` on sign-out: the install id stays (contract 6.1). The user is
   * stamped on each event when it happens, so a queued event never moves to another user.
   */
  setUser(userId: string | undefined, premium?: boolean): void {
    this.userId = userId;
    this.premium = premium;
  }

  track(name: string, props?: Record<string, EventPropValue>, options: TrackOptions = {}): void {
    if (this.stopped) {
      return;
    }
    const now = this.env.now();
    const newSession = this.touchSession(now);
    const { sessionEvent } = this.opts;
    if (newSession && sessionEvent !== undefined && sessionEvent.name !== name) {
      const first = this.firstSession && !this.hadSession;
      this.hadSession = true;
      this.track(sessionEvent.name, sessionEvent.props(first));
    }
    const event: QueuedEvent = {
      id: this.env.uuid(),
      name,
      at: now,
      sessionId: this.sessionId,
      ...(this.userId === undefined ? {} : { userId: this.userId }),
      ...(this.premium === undefined ? {} : { premium: this.premium }),
      ...options,
    };
    if (props !== undefined && Object.keys(props).length > 0) {
      event.props = props;
    }
    if (this.opts.debug === true) {
      console.log('[events]', name, event);
      return;
    }
    const queue = this.loadQueue();
    queue.push(event);
    if (queue.length > MAX_QUEUE) {
      queue.splice(0, queue.length - MAX_QUEUE);
    }
    this.saveQueue(queue);
    if (queue.length >= FLUSH_AT) {
      this.flush().catch(() => undefined);
    }
  }

  /** Consent withdrawn: stop, and forget the queue, the install and the session. */
  reset(): void {
    this.stopped = true;
    this.env.clearInterval(this.timer);
    this.memoryQueue = [];
    for (const key of STORAGE_KEYS) {
      try {
        this.opts.storage?.remove(EVENTS_STORAGE_PREFIX + key);
      } catch {
        // Blocked storage: nothing to forget.
      }
    }
  }

  async flush(): Promise<void> {
    if (this.stopped || this.sending || this.env.now() < this.notBefore || this.otherTabSends()) {
      return;
    }
    const batch = this.takeBatch();
    if (batch.length === 0) {
      return;
    }
    this.sending = true;
    this.write('sending', String(this.env.now()));
    try {
      const status = await this.post(batch);
      if (status === 204 || status === 400 || status === 413) {
        this.drop(batch);
        this.failures = 0;
        if (this.loadQueue().length >= FLUSH_AT) {
          queueMicrotask(() => {
            this.flush().catch(() => undefined);
          });
        }
      } else if (status !== 429) {
        this.backoff(status === 401 || status === 403 ? BACKOFF_MS.length : undefined);
      }
    } catch {
      this.backoff();
    } finally {
      this.sending = false;
      this.remove('sending');
    }
  }

  /** The page goes away: one batch goes to `sendBeacon`, which delivers after the page is gone. */
  flushOnExit(): void {
    if (this.stopped || this.sending || this.env.sendBeacon === undefined) {
      return;
    }
    const batch = this.takeBatch();
    if (batch.length === 0) {
      return;
    }
    const ok = this.env.sendBeacon(this.url(), new Blob([this.body(batch)], { type: 'text/plain' }));
    if (ok) {
      this.drop(batch);
    }
  }

  private url(): string {
    return `${this.opts.baseUrl}/v1/batch?key=${encodeURIComponent(this.opts.key)}`;
  }

  private async post(batch: QueuedEvent[]): Promise<number> {
    const res = await this.env.fetch(this.url(), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: this.body(batch),
      keepalive: true,
    });
    if (res.status === 429) {
      const after = Number(res.headers.get('Retry-After'));
      if (after > 0) {
        this.notBefore = this.env.now() + after * 1000;
      } else {
        this.backoff();
      }
    }
    return res.status;
  }

  private body(batch: QueuedEvent[]): string {
    const now = this.env.now();
    if (this.sessionId === '') {
      this.loadSession();
    }
    if (this.sessionId === '') {
      this.sessionId = this.env.uuid();
    }
    // The context carries no user: each event has its own (`track`). The session is the one of
    // the batch (`takeBatch` takes one session only).
    const { userId: _userId, premium: _premium, ...base } = this.opts.context;
    const sessionId = batch[0]?.sessionId ?? this.sessionId;
    const context = { ...base, installId: this.installId, sessionId };
    const events: WireEvent[] = batch.map(({ sessionId: _session, ...event }) => event);
    return JSON.stringify({ sentAt: now, context, events });
  }

  /**
   * The next batch: events of ONE session, at most 100 events and 60 KB. An event too big for any
   * batch can never go: it is dropped, so it does not block the queue.
   */
  private takeBatch(): QueuedEvent[] {
    const min = this.env.now() - MAX_AGE_MS;
    const all = this.loadQueue();
    const queue = all.filter((event) => event.at >= min && JSON.stringify(event).length + 513 <= BATCH_BYTES);
    if (queue.length !== all.length) {
      this.saveQueue(queue);
    }
    const out: QueuedEvent[] = [];
    let bytes = 512;
    const session = queue[0]?.sessionId;
    for (const event of queue) {
      const size = JSON.stringify(event).length + 1;
      if (out.length >= BATCH_EVENTS || bytes + size > BATCH_BYTES || event.sessionId !== session) {
        break;
      }
      out.push(event);
      bytes += size;
    }
    return out;
  }

  private drop(batch: QueuedEvent[]): void {
    const sent = new Set(batch.map((event) => event.id));
    this.saveQueue(this.loadQueue().filter((event) => !sent.has(event.id)));
  }

  /** True while another tab of this origin sends (its fresh send mark). */
  private otherTabSends(): boolean {
    const mark = Number(this.read('sending') ?? 0);
    return mark > 0 && this.env.now() - mark < SEND_MARK_MS;
  }

  private backoff(step?: number): void {
    this.failures = Math.min(step ?? this.failures + 1, BACKOFF_MS.length);
    this.notBefore = Math.max(this.notBefore, this.env.now() + (BACKOFF_MS[this.failures - 1] ?? 900_000));
  }

  private loadSession(): void {
    try {
      const stored = JSON.parse(this.read('session') ?? 'null') as { id: string; last: number } | null;
      if (stored !== null && typeof stored.id === 'string') {
        this.sessionId = stored.id;
        this.lastActivity = stored.last;
      }
    } catch {
      // A new session follows.
    }
  }

  /** Moves the session on to `now`. True when this starts a new session. */
  private touchSession(now: number): boolean {
    if (this.sessionId === '') {
      this.loadSession();
    }
    const isNew = this.sessionId === '' || now - this.lastActivity > SESSION_IDLE_MS;
    if (isNew) {
      this.sessionId = this.env.uuid();
    }
    this.lastActivity = now;
    this.write('session', JSON.stringify({ id: this.sessionId, last: now }));
    return isNew;
  }

  /** The queue. Read from storage each time: every tab of the origin shares one stored queue. */
  private loadQueue(): QueuedEvent[] {
    if (!this.storageOk) {
      return [...this.memoryQueue];
    }
    try {
      const parsed = JSON.parse(this.read('queue') ?? '[]') as unknown;
      return Array.isArray(parsed) ? (parsed as QueuedEvent[]) : [];
    } catch {
      return [];
    }
  }

  private saveQueue(queue: QueuedEvent[]): void {
    this.memoryQueue = queue;
    this.write('queue', JSON.stringify(queue));
  }

  private remove(key: string): void {
    try {
      this.opts.storage?.remove(EVENTS_STORAGE_PREFIX + key);
    } catch {
      // Blocked storage: nothing to remove.
    }
  }

  private read(key: string): string | null {
    try {
      return this.opts.storage?.get(EVENTS_STORAGE_PREFIX + key) ?? null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.opts.storage?.set(EVENTS_STORAGE_PREFIX + key, value);
    } catch {
      // Storage full or blocked: keep running from memory.
    }
  }
}
